import { createComponentLogger } from './logger';
import {
  auditBrowserStorage,
  cleanupRetiredRateQuoteStorage,
  safeCleanupSensitiveData
} from './securityCleanup';
import { performanceTracker } from './performanceTracker';

const log = createComponentLogger('StartupOrchestrator', 'initialization');

/**
 * Centralized startup orchestrator that manages all cleanup operations
 * in a single, efficient pass to improve startup performance
 */
export class StartupOrchestrator {
  private static instance: StartupOrchestrator | undefined;
  private cleanupComplete = false;
  
  /**
   * Initialize the startup orchestrator
   * This is idempotent - multiple calls will not re-run cleanup
   */
  static async initialize(): Promise<void> {
    if (this.instance?.cleanupComplete) {
      log.debug('Startup cleanup already complete, skipping');
      return;
    }
    
    if (!this.instance) {
      this.instance = new StartupOrchestrator();
    }
    
    await this.instance.runCleanupSequence();
  }
  
  /**
   * Run the complete cleanup sequence in an optimized order
   */
  private async runCleanupSequence(): Promise<void> {
    const startTime = performance.now();
    
    try {
      log.debug('Starting consolidated cleanup sequence');

      // Remove browser-only artifacts from the retired Tools > Rate Quote app.
      const retiredQuoteItemsCleaned = cleanupRetiredRateQuoteStorage();
      
      // Step 1: Perform security audit
      const auditResults = auditBrowserStorage();
      log.debug('Security audit complete', {
        totalItems: auditResults.summary.totalSensitiveItems,
        localStorage: auditResults.summary.localStorageSensitive,
        sessionStorage: auditResults.summary.sessionStorageSensitive
      });
      
      // Step 2: Clean sensitive data if needed
      if (auditResults.summary.totalSensitiveItems > 0) {
        log.debug('Cleaning sensitive data');
        safeCleanupSensitiveData();
      }
      
      // Mark cleanup as complete
      this.cleanupComplete = true;
      
      // Mark performance milestone
      performanceTracker.mark('cleanup_complete');
      
      // Single consolidated success log
      const duration = Math.round(performance.now() - startTime);
      log.info('Startup cleanup complete', {
        duration,
        itemsCleaned: auditResults.summary.totalSensitiveItems + retiredQuoteItemsCleaned,
        status: 'success'
      });
      
      // Expose performance metric in development
      if (import.meta.env.DEV) {
        Object.assign(window, { __startupCleanupDuration: duration });
      }
      
    } catch (error) {
      log.error('Startup cleanup failed', undefined, error as Error);
      // Mark as complete even on error to prevent retry loops
      this.cleanupComplete = true;
    }
  }
  
  /**
   * Check if cleanup has completed
   */
  static isComplete(): boolean {
    return this.instance?.cleanupComplete || false;
  }
  
  /**
   * Reset the orchestrator (mainly for testing)
   */
  static reset(): void {
    if (this.instance) {
      this.instance.cleanupComplete = false;
    }
    this.instance = undefined;
  }
}
